// FE-PINV-071 — <PipelineListView>.
//
// Pipeline-tab List view body. Mounted by RenewalBoardPanel's W1
// view-router inside the ``pipeline-view-body-list`` slot. Shares the
// toolbar + saved-view + cadence-visual + "+ New" infra with Grid
// (FE-SCRM-008) and Kanban (FE-PINV-070); adds bulk-action surface,
// density, and the 500-row safety rail.
//
// FE-PINV-072 hangs the multi-select column + sticky bulk-action bar
// off this view. FE-PINV-073 hangs the Columns popover off this view's
// "Columns" button.

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Table, THead, TBody, Tr, Th, Td } from "@/design-system";
import { CadenceHealthEdge } from "@/features/sales/components/CadenceHealthEdge";
import { MoneyCell } from "@/features/sales/components/MoneyCell";
import { OpportunityCardActionMenu } from "@/features/sales/components/OpportunityCardActionMenu";

import type { RenewalBoardResponse } from "../RenewalBoard/types";

import { ColumnsPopover } from "./ColumnsPopover";
import type { Density } from "./filterState";
import {
  type OpportunityListRow,
  flattenBoardToListRows,
  totalBoardOpportunityCount,
} from "./listRowFlatten";
import { sortListRows } from "./listSort";
import {
  DEFAULT_LOADED_ROWS,
  MAX_LOADED_ROWS,
  cycleSort,
  readListUrlState,
  writeListUrlState,
  type ListColumnId,
  type ListSortField,
} from "./listUrlState";
import type { ListSelectionApi } from "./useListSelection";


// ── Column metadata ─────────────────────────────────────────────────


interface ColumnSpec {
  id: ListColumnId;
  label: string;
  align?: "left" | "right";
  /** Sort field this column hands to ``cycleSort``. ``select`` has
   *  none. */
  sortField: ListSortField | null;
}


const COLUMN_SPECS: Record<ListColumnId, ColumnSpec> = {
  select: { id: "select", label: "", sortField: null },
  prospect: { id: "prospect", label: "Prospect", sortField: "prospect" },
  opportunity: {
    id: "opportunity", label: "Opportunity", sortField: "opportunity",
  },
  stage: { id: "stage", label: "Stage", sortField: "stage" },
  type: { id: "type", label: "Type", sortField: "type" },
  lives: { id: "lives", label: "Lives", sortField: "lives" },
  renewal: { id: "renewal", label: "Renewal", sortField: "renewal" },
  producer: { id: "producer", label: "Producer", sortField: "producer" },
  est_arr: {
    id: "est_arr", label: "Est ARR", align: "right", sortField: "est_arr",
  },
  last_activity: {
    id: "last_activity", label: "Last activity", sortField: "last_activity",
  },
  next_step: { id: "next_step", label: "Next step", sortField: "next_step" },
  alerts: { id: "alerts", label: "Alerts", sortField: "alerts" },
};


// ── Component ───────────────────────────────────────────────────────


export interface PipelineListViewProps {
  board: RenewalBoardResponse | undefined;
  showClosed: boolean;
  /** Opens W2's "+ Filter" popover via the host toolbar handle.
   *  Wired from RenewalBoardPanel; the footer's "Refine filters" link
   *  also calls this. */
  onOpenFilters: () => void;
  /** Selection API hoisted to the host so the bulk-action bar
   *  (FE-PINV-072) can mount as a sibling and share state. */
  selection: ListSelectionApi;
  /** FE-PINV-073 — opens W2's <SavedViewSwitcher> save flow with the
   *  current columns_json + density baked in. Host (RenewalBoardPanel)
   *  routes this through the canonical save path; the popover does
   *  not duplicate the save modal. */
  onSaveAsView?: (columns: {
    visible: ListColumnId[];
    order: ListColumnId[];
    density: Density;
  }) => void;
  /** Optional override for the "Columns" trigger render. Tests use
   *  this to assert mount-point + behaviour without the popover's
   *  full DOM. */
  renderColumnsTrigger?: () => JSX.Element;
  /** Map of producer Membership id → display name, used by the
   *  Producer cell to resolve ``row.producer_id`` to a label. Without
   *  this the cell falls back to "—" (better than the raw UUID the
   *  pre-FE-SCRM-064b list view leaked). RenewalBoardPanel already
   *  computes the source list via ``useTeamMembersQuery``; passes a
   *  Map so the lookup is O(1) per row. */
  producerLabels?: Map<string, string>;
}


export function PipelineListView({
  board,
  showClosed,
  onOpenFilters,
  selection,
  onSaveAsView,
  renderColumnsTrigger,
  producerLabels,
}: PipelineListViewProps): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const urlState = useMemo(() => readListUrlState(search), [search]);

  // Flatten + sort the board → rows.
  const flatRows = useMemo(
    () => flattenBoardToListRows(board, { show_closed: showClosed }),
    [board, showClosed],
  );
  const sortedRows = useMemo(
    () => sortListRows(flatRows, urlState.sort),
    [flatRows, urlState.sort],
  );

  const totalCount = useMemo(
    () => totalBoardOpportunityCount(board),
    [board],
  );
  const visibleRows = useMemo(
    () => sortedRows.slice(0, urlState.loadedRows),
    [sortedRows, urlState.loadedRows],
  );

  // Keep the selection hook's visible-order ref in sync with the
  // current render so shift-click range select works.
  useEffect(() => {
    selection.setVisibleOrder(visibleRows.map((r) => r.opportunity_id));
  }, [visibleRows, selection]);

  function updateUrlState(
    next: Partial<{
      sort: typeof urlState.sort;
      density: typeof urlState.density;
      visible: ListColumnId[];
      order: ListColumnId[];
      loadedRows: number;
    }>,
  ) {
    const merged = { ...urlState, ...next };
    setSearch(writeListUrlState(merged, search), { replace: true });
  }

  function onHeaderClick(col: ListColumnId) {
    const spec = COLUMN_SPECS[col];
    if (!spec.sortField) return;
    updateUrlState({ sort: cycleSort(urlState.sort, spec.sortField) });
  }

  function onLoadMore() {
    const next = Math.min(
      urlState.loadedRows + DEFAULT_LOADED_ROWS,
      MAX_LOADED_ROWS,
    );
    updateUrlState({ loadedRows: next });
  }

  const orderedVisibleCols = useMemo(() => {
    const visibleSet = new Set(urlState.visible);
    return urlState.order.filter((id) => visibleSet.has(id));
  }, [urlState.visible, urlState.order]);

  const allVisibleIds = visibleRows.map((r) => r.opportunity_id);
  const headerCheckboxState = computeHeaderCheckboxState(
    allVisibleIds, selection,
  );

  const safetyRailEngaged = urlState.loadedRows >= MAX_LOADED_ROWS;
  const moreToLoad = visibleRows.length < sortedRows.length;
  const rowHeightClass =
    urlState.density === "compact" ? "h-9" : "h-[52px]";

  return (
    <div data-testid="pipeline-list-view" className="flex flex-col gap-2">
      <div className="relative flex items-center justify-end gap-2">
        {renderColumnsTrigger
          ? renderColumnsTrigger()
          : (
            <ColumnsTrigger
              visible={urlState.visible}
              order={urlState.order}
              density={urlState.density}
              onChange={({ visible, order, density }) =>
                updateUrlState({ visible, order, density })
              }
              onSaveAsView={onSaveAsView}
            />
          )}
      </div>

      <Table
        data-testid="pipeline-list-table"
        className="min-w-full border-collapse"
        wrapperClassName="rounded-md border border-border-subtle"
      >
        <THead className="bg-recessed text-text-tertiary">
          <Tr>
            {orderedVisibleCols.map((id) => (
              <ListHeaderCell
                key={id}
                col={id}
                spec={COLUMN_SPECS[id]}
                sortField={urlState.sort.field}
                sortDir={urlState.sort.dir}
                onClick={() => onHeaderClick(id)}
                selectAllVisible={() =>
                  selection.setAllVisible(
                    allVisibleIds, headerCheckboxState !== "all",
                  )
                }
                headerCheckboxState={headerCheckboxState}
              />
            ))}
            {/* Row action menu column (no header label). */}
            <Th className="w-10" aria-hidden="true" />
          </Tr>
        </THead>
        <TBody>
          {visibleRows.map((row) => (
            <PipelineListRow
              key={row.opportunity_id}
              row={row}
              cols={orderedVisibleCols}
              rowHeightClass={rowHeightClass}
              producerLabels={producerLabels}
              isSelected={selection.isSelected(row.opportunity_id)}
              onToggleSelect={(e) => {
                // shiftKey is only meaningful on mouse / keyboard events;
                // ChangeEvent from a direct checkbox tick has no modifier.
                const shiftKey =
                  "shiftKey" in e ? Boolean(e.shiftKey) : false;
                selection.toggle(row.opportunity_id, { shiftKey });
              }}
            />
          ))}
          {visibleRows.length === 0 && (
            <Tr>
              <Td
                colSpan={orderedVisibleCols.length + 1}
                className="py-6 text-center text-xs text-text-tertiary"
                data-testid="pipeline-list-empty"
              >
                No opportunities match the current filters.
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>

      <ListFooter
        shownCount={visibleRows.length}
        totalCount={totalCount}
        safetyRailEngaged={safetyRailEngaged}
        moreToLoad={moreToLoad}
        onLoadMore={onLoadMore}
        onOpenFilters={onOpenFilters}
      />
    </div>
  );
}


// ── Header cell ─────────────────────────────────────────────────────


type HeaderCheckboxState = "none" | "some" | "all";


function computeHeaderCheckboxState(
  visibleIds: readonly string[], selection: ListSelectionApi,
): HeaderCheckboxState {
  if (visibleIds.length === 0) return "none";
  const selectedVisible = visibleIds.filter((id) => selection.isSelected(id));
  if (selectedVisible.length === 0) return "none";
  if (selectedVisible.length === visibleIds.length) return "all";
  return "some";
}


function ListHeaderCell({
  col, spec, sortField, sortDir, onClick,
  selectAllVisible, headerCheckboxState,
}: {
  col: ListColumnId;
  spec: ColumnSpec;
  sortField: ListSortField | null;
  sortDir: "asc" | "desc";
  onClick: () => void;
  selectAllVisible: () => void;
  headerCheckboxState: HeaderCheckboxState;
}): JSX.Element {
  if (col === "select") {
    return (
      <Th className="w-8 sticky left-0 z-10 bg-recessed !px-2">
        <input
          type="checkbox"
          data-testid="pipeline-list-select-all"
          aria-label="Select all visible rows"
          checked={headerCheckboxState === "all"}
          ref={(el) => {
            if (el) el.indeterminate = headerCheckboxState === "some";
          }}
          onChange={selectAllVisible}
        />
      </Th>
    );
  }
  const sorted = sortField === spec.sortField;
  const arrow = sorted ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const align = spec.align === "right" ? "text-right" : "text-left";
  return (
    <Th
      className={[
        "uppercase tracking-wide text-[10px]",
        align,
        "cursor-pointer select-none hover:text-text-primary",
      ].join(" ")}
      onClick={onClick}
      data-testid={`pipeline-list-header-${col}`}
      aria-sort={
        sorted ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      {spec.label}{arrow}
    </Th>
  );
}


// ── Row ─────────────────────────────────────────────────────────────


const _STAGE_LABEL: Record<string, string> = {
  lead: "Lead",
  meeting_set: "Meeting set",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  bor_signed_pending_effective_date: "BOR pending",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};


const _TYPE_LABEL: Record<string, string> = {
  new_business: "NEW",
  bor_takeover: "BOR",
  rfp_response: "RFP",
  expansion: "EXP",
};


const _LIVES_LABEL: Record<string, string> = {
  micro: "Micro",
  small: "Small",
  mid_market: "Mid",
  large: "Large",
  enterprise: "Enterprise",
};


const _MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];


function _relativeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const days = Math.round((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}


function PipelineListRow({
  row, cols, rowHeightClass, producerLabels, isSelected, onToggleSelect,
}: {
  row: OpportunityListRow;
  cols: ListColumnId[];
  rowHeightClass: string;
  producerLabels: Map<string, string> | undefined;
  isSelected: boolean;
  onToggleSelect: (e: React.MouseEvent | React.ChangeEvent) => void;
}): JSX.Element {
  return (
    <Tr
      data-testid={`pipeline-list-row-${row.opportunity_id}`}
      data-selected={isSelected}
      className={[
        "group relative border-t border-border-subtle hover:bg-recessed",
        isSelected ? "bg-teal-tint" : "",
      ].join(" ")}
    >
      {cols.map((id) => (
        <Td
          key={id}
          className={[
            rowHeightClass,
            id === "select" ? "sticky left-0 z-[5] bg-inherit" : "",
            id === "est_arr" ? "text-right tabular-nums" : "",
          ].join(" ")}
        >
          {renderCell(id, row, isSelected, onToggleSelect, producerLabels)}
        </Td>
      ))}
      <Td
        className={[
          rowHeightClass,
          "w-10 !px-1 text-right",
          "opacity-0 group-hover:opacity-100 focus-within:opacity-100",
        ].join(" ")}
        data-testid={`pipeline-list-row-actions-${row.opportunity_id}`}
      >
        <OpportunityCardActionMenu
          opportunity_id={row.opportunity_id}
          prospect_name={row.prospect_name}
          stage={row.stage}
          opportunity_type={row.opportunity_type}
          value_estimate={row.value_estimate}
        />
      </Td>
    </Tr>
  );
}


function renderCell(
  id: ListColumnId,
  row: OpportunityListRow,
  isSelected: boolean,
  onToggleSelect: (e: React.MouseEvent | React.ChangeEvent) => void,
  producerLabels: Map<string, string> | undefined,
): JSX.Element | string {
  switch (id) {
    case "select":
      return (
        <div className="flex items-center gap-2">
          <CadenceHealthEdge state={row.cadence_health} variant="row" />
          <input
            type="checkbox"
            aria-label={`Select ${row.prospect_name}`}
            checked={isSelected}
            onClick={(e) => onToggleSelect(e)}
            onChange={() => undefined}
            data-testid={`pipeline-list-select-${row.opportunity_id}`}
          />
        </div>
      );
    case "prospect":
      return (
        <Link
          to={`/dashboard/sales-crm/prospects/${row.prospect_id}`}
          className="text-text-primary underline-offset-2 hover:underline"
        >
          {row.prospect_name}
        </Link>
      );
    case "opportunity":
      return (
        <Link
          to={`/dashboard/sales-crm/opportunities/${row.opportunity_id}`}
          className="text-text-primary underline-offset-2 hover:underline"
        >
          {row.opportunity_name}
        </Link>
      );
    case "stage":
      return (
        <span className="inline-flex items-center rounded border border-border-subtle bg-raised px-1.5 py-0.5 text-[11px] text-text-secondary">
          {_STAGE_LABEL[row.stage] ?? row.stage}
        </span>
      );
    case "type":
      return (
        <span className="inline-flex items-center rounded bg-recessed px-1.5 py-0.5 font-mono text-[10px] uppercase text-text-secondary">
          {_TYPE_LABEL[row.opportunity_type] ?? row.opportunity_type}
        </span>
      );
    case "lives":
      return row.lives_band ? _LIVES_LABEL[row.lives_band] ?? row.lives_band : "—";
    case "renewal":
      return (
        <span className="font-mono text-xs tabular-nums text-text-secondary">
          {(_MONTH_SHORT[row.renewal_month - 1] ?? row.renewal_month)
            + " "
            + String(row.renewal_year).slice(2)}
        </span>
      );
    case "producer":
      if (!row.producer_id) return "—";
      return producerLabels?.get(row.producer_id) ?? "—";
    case "est_arr":
      return <MoneyCell cents={row.value_estimate} />;
    case "last_activity":
      return _relativeAgo(row.last_activity_at);
    case "next_step":
      return (
        <span
          className="block max-w-[16rem] truncate text-text-secondary"
          title={row.next_step ?? ""}
        >
          {row.next_step ?? "—"}
        </span>
      );
    case "alerts":
      return row.form5500_alert_count > 0 ? (
        <span className="inline-flex items-center rounded bg-ember-tint px-1.5 py-0.5 text-[11px] text-text-primary">
          ⚑ {row.form5500_alert_count}
        </span>
      ) : "—";
  }
}


// ── Footer ──────────────────────────────────────────────────────────


function ListFooter({
  shownCount, totalCount, safetyRailEngaged, moreToLoad,
  onLoadMore, onOpenFilters,
}: {
  shownCount: number;
  totalCount: number;
  safetyRailEngaged: boolean;
  moreToLoad: boolean;
  onLoadMore: () => void;
  onOpenFilters: () => void;
}): JSX.Element {
  return (
    <div
      data-testid="pipeline-list-footer"
      className="flex items-center justify-between gap-3 px-3 py-2 text-xs text-text-tertiary"
    >
      <span data-testid="pipeline-list-count">
        Showing {shownCount} of {totalCount}
      </span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenFilters}
          className="text-text-secondary underline-offset-2 hover:underline"
          data-testid="pipeline-list-refine-filters"
        >
          Refine filters
        </button>
        {safetyRailEngaged ? (
          <button
            type="button"
            onClick={onOpenFilters}
            data-testid="pipeline-list-safety-rail-switch"
            className="rounded-md border border-amber bg-amber-tint px-2 py-1 text-xs text-text-primary"
          >
            Switch to refined filters
          </button>
        ) : moreToLoad ? (
          <button
            type="button"
            onClick={onLoadMore}
            data-testid="pipeline-list-load-more"
            className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-primary hover:border-text-tertiary"
          >
            Load more
          </button>
        ) : null}
      </div>
    </div>
  );
}


// Public side-export so FE-PINV-073's Columns popover can call the
// same canonical reset behavior. Currently nothing imports it; placed
// here so FE-PINV-073 doesn't redefine the literal column list.
export { COLUMN_SPECS };


// ── Columns trigger (FE-PINV-073) ───────────────────────────────────


function ColumnsTrigger({
  visible, order, density, onChange, onSaveAsView,
}: {
  visible: ListColumnId[];
  order: ListColumnId[];
  density: Density;
  onChange: (next: {
    visible: ListColumnId[];
    order: ListColumnId[];
    density: Density;
  }) => void;
  onSaveAsView?: (cols: {
    visible: ListColumnId[];
    order: ListColumnId[];
    density: Density;
  }) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="pipeline-list-columns-trigger"
        className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs font-medium text-text-primary hover:border-text-tertiary"
      >
        Columns
      </button>
      {open ? (
        <ColumnsPopover
          visible={visible}
          order={order}
          density={density}
          onChange={onChange}
          onSaveAsView={(cols) => {
            setOpen(false);
            onSaveAsView?.(cols);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}
